<template>
  <section id="how-it-works" class="how" aria-labelledby="how-title">
    <div class="how-bg" aria-hidden="true"></div>

    <div class="how-inner">
      <header class="how-head" data-reveal="up">
        <span class="how-eyebrow">{{ $t('landing.how_label') }}</span>
        <h2 id="how-title" class="how-title">
          {{ $t('landing.how_title_pre') }}
          <span class="grad">{{ $t('landing.how_title_highlight') }}</span>
        </h2>
        <p class="how-sub">{{ $t('landing.how_subtitle') }}</p>
      </header>

      <ol class="how-steps">
        <li
          v-for="(step, i) in steps"
          :key="step.key"
          class="how-step"
          data-reveal="up"
          :data-reveal-delay="i + 1"
        >
          <span class="step-num">{{ String(i + 1).padStart(2, '0') }}</span>
          <span class="step-icon" :class="`accent-${step.accent}`" aria-hidden="true">
            <NuxtIcon :name="step.icon" />
          </span>
          <h3 class="step-title">{{ $t(`landing.how_step_${i + 1}_title`) }}</h3>
          <p class="step-desc">{{ $t(`landing.how_step_${i + 1}_desc`) }}</p>
        </li>
      </ol>

      <div class="how-cta" data-reveal="up">
        <NuxtLink :to="localePath('/auth') + '?tab=signup'" class="btn-cyber-primary btn-how">
          {{ $t('landing.cta_signup') }}
        </NuxtLink>
      </div>
    </div>
  </section>
</template>

<script setup>
const localePath = useLocalePath()

// Upload → release → payout. Icons are mdi (bundled collection).
const steps = [
  { key: 'upload', icon: 'mdi:cloud-upload-outline', accent: 'cyan' },
  { key: 'release', icon: 'mdi:earth', accent: 'coral' },
  { key: 'paid', icon: 'mdi:cash-multiple', accent: 'cyan' }
]
</script>

<style scoped>
/* Mobile-first. Ladder: 480 / 640 / 768 / 1024 / 1280 (variables.css). */

.how {
  position: relative;
  padding: var(--landing-section-pad) 0;
  overflow: hidden;
}

.how-bg {
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse 70% 50% at 50% 100%, rgb(var(--secondary-channels) / 0.05), transparent 65%);
  pointer-events: none;
}

.how-inner {
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--space-6);
}

.how-head {
  text-align: center;
  max-width: 720px;
  margin: 0 auto var(--space-10);
}

.how-eyebrow {
  display: inline-block;
  font-family: var(--font-heading);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: var(--primary);
  margin-bottom: var(--space-4);
}

.how-title {
  font-family: var(--font-heading);
  font-size: var(--landing-title-size);
  font-weight: 800;
  line-height: 1.05;
  text-transform: uppercase;
  letter-spacing: -0.01em;
  margin: 0 0 var(--space-4);
  text-wrap: balance;
}

.grad {
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.how-sub {
  font-size: var(--landing-sub-size);
  line-height: 1.6;
  color: var(--text-muted);
  margin: 0;
}

.how-steps {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-4);
  counter-reset: none;
}

@media (min-width: 768px) {
  .how-steps {
    grid-template-columns: repeat(3, 1fr);
    gap: var(--space-5);
  }
}

.how-step {
  position: relative;
  padding: var(--space-8) var(--space-6) var(--space-6);
  border-radius: var(--radius-xl);
  border: 1px solid var(--border);
  background: var(--surface-color);
  text-align: left;
  transition: border-color 0.28s ease, transform 0.28s ease, box-shadow 0.28s ease;
}

.how-step:hover {
  transform: translateY(-4px);
  border-color: rgb(var(--primary-channels) / 0.35);
  box-shadow: 0 20px 50px -28px var(--primary-glow);
}

/* connector arrows between the cards, desktop only */
@media (min-width: 768px) {
  .how-step:not(:last-child)::after {
    content: '→';
    position: absolute;
    top: 50%;
    right: calc(-1 * var(--space-5) / 2 - 8px);
    transform: translateY(-50%);
    font-size: 1.1rem;
    color: var(--text-dim);
    z-index: 1;
  }
}

.step-num {
  position: absolute;
  top: var(--space-5);
  right: var(--space-5);
  font-family: var(--font-heading);
  font-size: 0.9rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  color: var(--text-dim);
  opacity: 0.5;
}

.step-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  margin-bottom: var(--space-5);
  font-size: 1.5rem;
}

.step-icon.accent-cyan {
  color: var(--primary);
  background: rgb(var(--primary-channels) / 0.12);
  border: 1px solid rgb(var(--primary-channels) / 0.3);
}

.step-icon.accent-coral {
  color: var(--secondary);
  background: rgb(var(--secondary-channels) / 0.12);
  border: 1px solid rgb(var(--secondary-channels) / 0.3);
}

.step-title {
  font-family: var(--font-heading);
  font-size: 1.2rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.01em;
  margin: 0 0 var(--space-2);
  color: var(--text-main);
}

.step-desc {
  font-size: 0.92rem;
  line-height: 1.6;
  color: var(--text-muted);
  margin: 0;
}

.how-cta {
  display: flex;
  justify-content: center;
  margin-top: var(--space-10);
}

.btn-how {
  height: 48px !important;
  padding: 0 32px !important;
  font-size: 0.75rem !important;
  width: 100%;
  max-width: 300px;
}

@media (min-width: 480px) {
  .btn-how {
    width: auto;
  }
}

.btn-how:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 3px;
}
</style>
